import { useCallback, useEffect, useState } from "react";
import {
  FiAlertTriangle,
  FiChevronLeft,
  FiChevronRight,
  FiLoader,
  FiRefreshCw,
  FiRotateCcw,
  FiSearch,
  FiUsers,
} from "react-icons/fi";
import {
  getAdminTransactions,
  adminRefundTransaction,
  getAdminSoldProducts,
  adminRefundProduct,
} from "../api/admin";
import { adminTheme } from "../styles/adminTheme";

/**
 * Every payment, and the refunds staff can issue against them.
 *
 * A refund asks nothing of the buyer: Keepz returns the money to whatever they paid
 * with, and the server settles Mintly's side in the same call — access is revoked, the
 * seller's credit clawed back, commission reversed.
 *
 * Only product purchases can be refunded, and never a subscription: its sign-up order
 * is for 0 — Keepz insists on that — and the money is taken by their plan afterwards,
 * so refunding the order would hand back nothing while revoking access. The server
 * decides this per row and says why; the table only renders the verdict.
 *
 * @see mintly-back/src/controllers/adminPaymentsController
 */

const TYPES = [
  { value: "", label: "All types" },
  { value: "product_purchase", label: "Product purchase" },
  { value: "subscription_cycle", label: "Subscription renewal" },
  { value: "topup", label: "Wallet top-up" },
  { value: "campaign_fund", label: "Campaign funding" },
  { value: "withdrawal", label: "Withdrawal" },
];

const STATUSES = [
  { value: "", label: "Any status" },
  { value: "SUCCESS", label: "Paid" },
  { value: "PENDING", label: "Pending" },
  { value: "FAILED", label: "Failed" },
  { value: "CANCELED", label: "Canceled" },
  { value: "EXPIRED", label: "Expired" },
  { value: "REFUND_REQUESTED", label: "Refund requested" },
  { value: "REFUNDED_BY_OPERATOR", label: "Refunded" },
];

/** Statuses that read as money in, so the table can colour them apart. */
const GOOD = ["SUCCESS"];
const GONE = [
  "REFUNDED_BY_OPERATOR",
  "REFUNDED_BY_KEEPZ",
  "REFUNDED_BY_INTEGRATOR",
  "REFUND_REQUESTED",
  "PARTIALLY_REFUNDED",
];

const money = (amount, currency = "GEL") =>
  `${currency === "GEL" ? "₾" : `${currency} `}${Number(amount || 0).toFixed(2)}`;

const when = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
};

const label = (list, value) => list.find((x) => x.value === value)?.label || value;

const AdminTransactionsTab = ({ isDark, setToast, baseText, mutedText, cardBase }) => {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", type: "", status: "", from: "", to: "" });
  const [search, setSearch] = useState("");
  /** The order currently being refunded, so only its own button spins. */
  const [refunding, setRefunding] = useState(null);

  const [products, setProducts] = useState([]);
  const [productQuery, setProductQuery] = useState("");
  const [bulk, setBulk] = useState(null); // { product, preview, running, results }

  const theme = adminTheme(isDark);
  const { inputBase, rowBorder } = theme;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminTransactions({ ...filters, page, limit: 50 });
      setRows(res.data || []);
      setTotals(res.totals || null);
      setPages(res.pages || 1);
    } catch (err) {
      setToast?.(err?.response?.data?.message || "Could not load transactions");
    } finally {
      setLoading(false);
    }
  }, [filters, page, setToast]);

  useEffect(() => {
    load();
  }, [load]);

  const loadProducts = useCallback(async () => {
    try {
      const res = await getAdminSoldProducts(productQuery);
      setProducts(res.data || []);
    } catch (err) {
      setToast?.(err?.response?.data?.message || "Could not load products");
    }
  }, [productQuery, setToast]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const applySearch = (e) => {
    e.preventDefault();
    setPage(1);
    setFilters((prev) => ({ ...prev, q: search.trim() }));
  };

  const setFilter = (key, value) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const refundRow = async (row) => {
    const who = row.user?.username || row.user?.email || "this buyer";
    const ok = window.confirm(
      `Refund ${money(row.amount, row.currency)} to ${who}?\n\n` +
        `Keepz returns the money to what they paid with. Their access to ` +
        `${row.product?.name || "the product"} ends and the seller's balance is reduced.`
    );
    if (!ok) return;

    setRefunding(row.orderId);
    try {
      await adminRefundTransaction(row.orderId);
      setToast?.(`Refunded ${money(row.amount, row.currency)} to ${who}`);
      await load();
      await loadProducts();
    } catch (err) {
      setToast?.(err?.response?.data?.message || "Refund failed");
    } finally {
      setRefunding(null);
    }
  };

  /** Step one of a product-wide refund: ask the server what it would do. */
  const previewBulk = async (product) => {
    setBulk({ product, preview: null, running: true, results: null });
    try {
      const res = await adminRefundProduct(product.productId, false);
      setBulk({ product, preview: res.preview, running: false, results: null });
    } catch (err) {
      setBulk(null);
      setToast?.(err?.response?.data?.message || "Could not prepare the refund");
    }
  };

  const runBulk = async () => {
    if (!bulk?.product) return;
    setBulk((prev) => ({ ...prev, running: true }));
    try {
      const res = await adminRefundProduct(bulk.product.productId, true);
      setBulk((prev) => ({ ...prev, running: false, results: res.refunded }));
      setToast?.(
        `Refunded ${res.refunded?.succeeded || 0} of ${res.refunded?.attempted || 0} buyers`
      );
      await load();
      await loadProducts();
    } catch (err) {
      setBulk((prev) => ({ ...prev, running: false }));
      setToast?.(err?.response?.data?.message || "Bulk refund failed");
    }
  };

  return (
    <div className="space-y-6">
      {/* ------------------------------------------------ every transaction */}
      <div className={`${cardBase} rounded-2xl p-5 sm:p-6`}>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
          <div>
            <h3 className={`text-base font-semibold tracking-tight ${baseText}`}>Transactions</h3>
            <p className={`text-xs ${mutedText} mt-1`}>
              Every payment through Keepz. Refunds go back to the buyer automatically.
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            className={`${theme.btnSecondary}`}
          >
            <FiRefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-5">
          <form onSubmit={applySearch} className="relative flex-1 min-w-[240px]">
            <FiSearch className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${mutedText}`} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Order id, username or email"
              className={`w-full rounded-xl border pl-9 pr-3 py-2 text-sm outline-none ${inputBase}`}
            />
          </form>
          <select
            value={filters.type}
            onChange={(e) => setFilter("type", e.target.value)}
            className={`rounded-xl border px-3 py-2 text-sm outline-none ${inputBase}`}
          >
            {TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          <select
            value={filters.status}
            onChange={(e) => setFilter("status", e.target.value)}
            className={`rounded-xl border px-3 py-2 text-sm outline-none ${inputBase}`}
          >
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          <input
            type="date"
            value={filters.from}
            onChange={(e) => setFilter("from", e.target.value)}
            className={`rounded-xl border px-3 py-2 text-sm outline-none ${inputBase}`}
          />
          <input
            type="date"
            value={filters.to}
            onChange={(e) => setFilter("to", e.target.value)}
            className={`rounded-xl border px-3 py-2 text-sm outline-none ${inputBase}`}
          />
        </div>

        {totals && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            {[
              ["Transactions", String(totals.count)],
              ["Gross", money(totals.gross)],
              ["Keepz fees", money(totals.keepzFees)],
              ["Reached us", money(totals.net)],
            ].map(([k, v]) => (
              <div key={k} className={`rounded-xl p-4 ${theme.inset}`}>
                <div className={`text-xs ${mutedText}`}>{k}</div>
                <div className={`text-xl font-bold ${baseText} mt-1`}>{v}</div>
              </div>
            ))}
          </div>
        )}

        <div className={theme.tableWrap}>
          <table className="w-full text-sm">
            <thead>
              <tr className={`text-left ${theme.thead}`}>
                {["When", "Buyer", "What", "Method", "Amount", "Status", ""].map((h) => (
                  <th key={h} className={theme.th}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className={`border-t ${rowBorder} ${theme.rowHover} align-top`}>
                  <td className={`px-4 py-3 whitespace-nowrap ${mutedText}`}>
                    <div>{when(row.createdAt)}</div>
                    <div className="font-mono text-[11px] opacity-70">
                      {row.orderId.slice(0, 8)}…
                    </div>
                  </td>
                  <td className={`px-4 py-3 ${baseText}`}>
                    <div>{row.user?.username || "—"}</div>
                    <div className={`text-[11px] ${mutedText}`}>{row.user?.email || ""}</div>
                  </td>
                  <td className={`px-4 py-3 ${baseText}`}>
                    <div>{row.product?.name || label(TYPES, row.type)}</div>
                    <div className={`text-[11px] ${mutedText}`}>
                      {row.mintSpace?.name || row.description || ""}
                    </div>
                  </td>
                  <td className={`px-4 py-3 ${mutedText}`}>{row.paymentMethod || "—"}</td>
                  <td className={`px-4 py-3 whitespace-nowrap ${baseText}`}>
                    <div className="font-semibold">{money(row.amount, row.currency)}</div>
                    {row.purchase?.isSubscription && (
                      <div className={`text-[11px] ${mutedText}`}>
                        subscription
                        {/* A sign-up order of 0 is an old one: Keepz used to take a
                            subscription's money only as renewals, so what the
                            subscription is worth is shown instead. */}
                        {row.amount === 0 ? ` · ${money(row.purchase.amount, row.currency)}` : ""}
                        {row.product?.period ? ` / ${row.product.period}` : ""}
                      </div>
                    )}
                    {row.keepzFee != null && (
                      <div className={`text-[11px] ${mutedText}`}>
                        fee {money(row.keepzFee, row.currency)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-block rounded-lg px-2 py-1 text-[11px] font-semibold ${
                        GOOD.includes(row.status)
                          ? "bg-mint/15 text-mint"
                          : GONE.includes(row.status)
                            ? "bg-amber-500/15 text-amber-500"
                            : "bg-fill-tertiary text-label-secondary"
                      }`}
                    >
                      {label(STATUSES, row.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {row.canRefund && (
                      <button
                        type="button"
                        onClick={() => refundRow(row)}
                        disabled={refunding === row.orderId}
                        title={`Refund ${money(row.amount, row.currency)} to ${row.user?.username || "the buyer"}`}
                        className={`flex items-center gap-2 rounded-xl border px-3 py-1.5 text-xs font-semibold transition disabled:opacity-50 ${rowBorder} ${baseText}`}
                      >
                        {refunding === row.orderId ? (
                          <FiLoader className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <FiRotateCcw className="h-3.5 w-3.5" />
                        )}
                        Refund
                      </button>
                    )}
                    {!row.canRefund && row.refundBlockedReason && (
                      <span className={`text-[11px] ${mutedText}`}>{row.refundBlockedReason}</span>
                    )}
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className={`py-10 text-center ${mutedText}`}>
                    No transactions match this filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between mt-4">
          <span className={`text-xs ${mutedText}`}>
            Page {page} of {pages}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className={`${theme.iconBtn} disabled:opacity-40`}
              aria-label="Previous page"
            >
              <FiChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              disabled={page >= pages}
              className={`${theme.iconBtn} disabled:opacity-40`}
              aria-label="Next page"
            >
              <FiChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* --------------------------------------------- refund a whole product */}
      <div className={`${cardBase} rounded-2xl p-5 sm:p-6`}>
        <h3 className={`text-base font-semibold tracking-tight ${baseText}`}>Refund everyone who bought a product</h3>
        <p className={`text-xs ${mutedText} mt-1 mb-4`}>
          Every buyer gets their money back and loses access. You'll see what it will do
          before anything is sent.
        </p>

        <input
          value={productQuery}
          onChange={(e) => setProductQuery(e.target.value)}
          placeholder="Filter by product or mint space"
          className={`w-full max-w-md rounded-xl border px-3 py-2 text-sm outline-none mb-4 ${inputBase}`}
        />

        <div className="space-y-2 max-h-[420px] overflow-y-auto">
          {products.map((p) => (
            <div
              key={p.productId}
              className={`flex items-center justify-between gap-4 rounded-xl p-4 ${theme.inset}`}
            >
              <div className="min-w-0">
                <div className={`font-semibold ${baseText}`}>{p.name}</div>
                <div className={`text-xs ${mutedText}`}>
                  {p.mintSpace.name} · {p.buyers} {p.buyers === 1 ? "buyer" : "buyers"} ·{" "}
                  {money(p.total, p.currency)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => previewBulk(p)}
                className={`flex shrink-0 items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold ${rowBorder} ${baseText}`}
              >
                <FiUsers className="h-4 w-4" />
                Refund all
              </button>
            </div>
          ))}
          {products.length === 0 && (
            <p className={`py-6 text-center text-sm ${mutedText}`}>Nothing sold yet.</p>
          )}
        </div>
      </div>

      {/* ------------------------------------------------------ confirm bulk */}
      {bulk && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label="Confirm refunding every buyer"
        >
          <div
            className={`w-full max-w-lg rounded-2xl border p-6 shadow-2xl ${
              "bg-bg-raised border-separator-default"
            }`}
          >
            <div className="flex items-start gap-3 mb-4">
              <FiAlertTriangle className="h-6 w-6 shrink-0 text-amber-500" />
              <div>
                <h4 className={`font-bold ${baseText}`}>{bulk.product.name}</h4>
                <p className={`text-xs ${mutedText}`}>{bulk.product.mintSpace.name}</p>
              </div>
            </div>

            {bulk.results ? (
              <div className="space-y-3">
                <p className={`text-sm ${baseText}`}>
                  Refunded {bulk.results.succeeded} of {bulk.results.attempted}.
                  {bulk.results.failed > 0 && ` ${bulk.results.failed} failed.`}
                </p>
                {bulk.results.failed > 0 && (
                  <div className={`max-h-48 overflow-y-auto rounded-xl border p-3 ${rowBorder}`}>
                    {bulk.results.results
                      .filter((r) => !r.ok)
                      .map((r) => (
                        <div key={r.orderId} className={`text-xs ${mutedText} mb-1`}>
                          <span className="font-mono">{r.orderId.slice(0, 8)}…</span> {r.message}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            ) : bulk.preview ? (
              <div className="space-y-3">
                <p className={`text-sm ${baseText}`}>
                  This refunds <strong>{bulk.preview.count}</strong>{" "}
                  {bulk.preview.count === 1 ? "buyer" : "buyers"} a total of{" "}
                  <strong>{money(bulk.preview.total, bulk.preview.currency)}</strong>. Each
                  one loses access, and the seller's balance is reduced by their share.
                </p>
                <div className={`max-h-48 overflow-y-auto rounded-xl border p-3 ${rowBorder}`}>
                  {bulk.preview.buyers.map((b) => (
                    <div key={b.orderId} className={`flex justify-between text-xs ${mutedText} mb-1`}>
                      <span>{b.user?.username || b.user?.email || b.orderId.slice(0, 8)}</span>
                      <span>{money(b.amount, bulk.preview.currency)}</span>
                    </div>
                  ))}
                </div>
                {bulk.preview.skippedCount > 0 && (
                  <p className={`text-xs ${mutedText}`}>
                    {bulk.preview.skippedCount}{" "}
                    {bulk.preview.skippedCount === 1 ? "buyer is" : "buyers are"} left out —
                    subscriptions are cancelled rather than refunded.
                  </p>
                )}
              </div>
            ) : (
              <div className={`flex items-center gap-2 py-6 text-sm ${mutedText}`}>
                <FiLoader className="h-4 w-4 animate-spin" />
                Working out what this would do…
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setBulk(null)}
                disabled={bulk.running}
                className={`${theme.btnSecondary} px-4`}
              >
                {bulk.results ? "Close" : "Cancel"}
              </button>
              {!bulk.results && (
                <button
                  type="button"
                  onClick={runBulk}
                  disabled={bulk.running || !bulk.preview?.count}
                  className="flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-2 text-sm font-bold text-ink disabled:opacity-50"
                >
                  {bulk.running && <FiLoader className="h-4 w-4 animate-spin" />}
                  Refund {bulk.preview?.count || 0}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTransactionsTab;
